import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Typography } from '@mui/material';
import { useSystemNotificationListQuery } from '../../../types/generated-types';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { Add } from '@mui/icons-material';
import { Actions } from '../../auth/types/ability';

// Use the generated type instead of defining our own interface
type SystemNotification = NonNullable<
  NonNullable<
    ReturnType<typeof useSystemNotificationListQuery>['data']
  >['systemNotifications']
>[0];

export const SystemNotificationList: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const { setContextMenuItems, setTitle } = useInjectableComponents();

  const { data, loading, error } = useSystemNotificationListQuery();

  // Set the page title
  useEffect(() => {
    setTitle('System Notifications');
    return () => {
      setTitle(undefined);
    };
  }, [setTitle]);

  // Set the context menu items
  useEffect(() => {
    const bottomMenu = [
      {
        items: [
          {
            id: 'menu-item-add-system-notification',
            label: 'Create System Notification',
            icon: <Add fontSize="small" />,
            action: () => navigate('/system-notifications/create'),
            permit: {
              action: 'create' as Actions,
              subject: { __typename: 'SystemNotification', _id: '' },
            },
          },
        ],
      },
    ];
    setContextMenuItems(bottomMenu);
    return () => {
      setContextMenuItems(undefined);
    };
  }, [navigate, setContextMenuItems]);

  const columns: MRT_ColumnDef<SystemNotification>[] = [
    {
      accessorKey: 'message',
      header: 'Message',
      size: 300,
      maxSize: 400,
    },
    {
      accessorKey: 'relatedEntityName',
      header: 'Related Entity',
      size: 200,
      maxSize: 250,
    },
    {
      accessorKey: 'entityType',
      header: 'Entity Type',
      size: 120,
      maxSize: 150,
    },

    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 120,
      maxSize: 150,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<number>();
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return (
          <div style={{ lineHeight: '1.2' }}>
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.9em', color: 'rgba(0,0,0,0.6)' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'displayStart',
      header: 'Display Start',
      size: 120,
      maxSize: 150,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<number>();
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return (
          <div style={{ lineHeight: '1.2' }}>
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.9em', color: 'rgba(0,0,0,0.6)' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'displayEnd',
      header: 'Display End',
      size: 120,
      maxSize: 150,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<number>();
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return (
          <div style={{ lineHeight: '1.2' }}>
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.9em', color: 'rgba(0,0,0,0.6)' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'acknowledgedAt',
      header: 'Acknowledged',
      size: 120,
      maxSize: 150,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<number>();
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return (
          <div style={{ lineHeight: '1.2' }}>
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.9em', color: 'rgba(0,0,0,0.6)' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'seenAt',
      header: 'Seen At',
      size: 120,
      maxSize: 150,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<number>();
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return (
          <div style={{ lineHeight: '1.2' }}>
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.9em', color: 'rgba(0,0,0,0.6)' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
  ];

  const systemNotifications = (data?.systemNotifications || []).map(
    (notification) => {
      const newNotification = {
        ...notification,
        acknowledgedAt:
          notification.acknowledgedAt === 1
            ? null
            : notification.acknowledgedAt,
      };
      return newNotification;
    },
  );

  if (loading) {
    return (
      <div>
        <Typography variant="h6">Loading system notifications...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Typography variant="h6" color="error">
          Failed to load system notifications
        </Typography>
      </div>
    );
  }

  const handleRowClick = (row: SystemNotification) => {
    navigate(`/system-notifications/${row._id}`);
  };

  return (
    <div style={{ padding: '20px' }}>
      <MaterialReactTable
        columns={columns}
        data={systemNotifications}
        state={{ isLoading: loading }}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        muiTableBodyRowProps={{
          hover: true,
          onClick: (e) => {
            const row = e.currentTarget.closest('tr');
            if (row) {
              const rowIndex = Array.from(
                row.parentElement?.children || [],
              ).indexOf(row);
              if (rowIndex > 0) {
                // Skip header row
                handleRowClick(systemNotifications[rowIndex - 1]);
              }
            }
          },
          sx: { cursor: 'pointer' },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
          },
        }}
        layoutMode="semantic"
      />
    </div>
  );
};
